import { DSScreen, Order } from 'kiolyn-api';
import { isEmpty } from 'lodash';
import { decorate, observable } from 'mobx';
import { useLocalStore } from 'mobx-react-lite';
import React, { createContext, FC, ReactElement, useContext } from 'react';
import { isNullOrUndefined } from 'util';
import { ViewState } from '../domain/models/ViewState';
import IMainStationRepository from '../domain/repositories/IMainStationRepository';
import IScreenRepository from '../domain/repositories/IScreenRepository';
import IStationRepository from '../domain/repositories/IStationRepository';
import IStatusBoardRepository from '../domain/repositories/IStatusBoardRepository';
import MainStationRepository from '../interfaces/MainStationRepository';

class ScreenStore {
  private readonly screenRepository?: IScreenRepository;
  private readonly stationRepository?: IStationRepository;
  private readonly statusBoardRepository?: IStatusBoardRepository;
  private mainStationRepository?: IMainStationRepository;
  private readonly storeid: string;
  private readonly screenid: string;

  private websocket?: WebSocket;

  // The main screen view state
  public viewState = ViewState.LOADING;
  // The current Screen
  public screen: DSScreen = {};
  // The orders for status board
  public orders: Order[] = [];
  public hasStatusBoard = false;

  constructor(
    storeid: string,
    screenid: string,
    screenRepository?: IScreenRepository,
    stationRepository?: IStationRepository,
    statusBoardRepository?: IStatusBoardRepository
  ) {
    this.screenRepository = screenRepository;
    this.stationRepository = stationRepository;
    this.statusBoardRepository = statusBoardRepository;
    this.storeid = storeid;
    this.screenid = screenid;
  }

  // Reload the screen based on given storeid and screenid
  public async reload(): Promise<void> {
    if (isNullOrUndefined(this.screenRepository)) {
      return;
    }
    this.viewState = ViewState.LOADING;
    try {
      this.screen = await this.screenRepository.loadScreen(
        this.storeid,
        this.screenid
      );

      this.hasStatusBoard =
        this.screen.columns?.find(
          c => c.cells?.find(cell => cell.type === 'statusboard') !== undefined
        ) !== undefined;
      if (this.hasStatusBoard) {
        await this.loadMainStation();
        await this.listOrders();
        await this.connectToPC();
      }

      this.viewState = ViewState.OK;
    } catch (e) {
      //console.error(e); for pass test
      this.viewState = ViewState.ERROR;
    }
  }

  private async loadMainStation() {
    if (this.stationRepository === undefined) {
      return;
    }

    let mainStation = await this.stationRepository.loadMainStation(
      this.storeid
    );
    if (
      mainStation === undefined ||
      mainStation.ip_address === undefined ||
      isEmpty(mainStation.ip_address)
    ) {
      console.error('Main station is not found');
      return;
    }

    console.log(`Main station ip: ${mainStation.ip_address}`);

    this.mainStationRepository = new MainStationRepository(
      mainStation.ip_address,
      this.storeid
    );
  }

  private async listOrders() {
    if (this.mainStationRepository === undefined) {
      return;
    }

    try {
      this.orders = await this.mainStationRepository.listOrders();
    } catch {}
  }

  private async connectToPC() {
    if (this.mainStationRepository === undefined) {
      return;
    }

    let connectInterval: NodeJS.Timeout;
    this.websocket = this.mainStationRepository.getWebsocket();

    this.websocket.onopen = () => {
      clearTimeout(connectInterval);
      console.log('Connect to main station successfully');
    };

    // websocket onclose event listener
    this.websocket.onclose = e => {
      connectInterval = setTimeout(() => {
        if (this.websocket?.readyState === WebSocket.CLOSED) {
          this.connectToPC();
        }
      }, 1000);
    };

    // websocket onerror event listener
    this.websocket.onerror = err => {
      this.websocket?.close();
    };

    this.websocket.onmessage = async message => {
      const data = JSON.parse(message.data);
      if (data.type === 'OrderChanged' && data.content.includes('_')) {
        await this.handleOrderChanged(data);
      } else if (
        data.type === 'ActiveShiftChanged' &&
        data.content.includes('close')
      ) {
        await this.handleActiveShiftChanged();
      }
    };
  }

  private async playSoundForOrder(order: Order) {
    if (
      this.statusBoardRepository === undefined ||
      order.instore_status !== 'READY' ||
      order.order_no === undefined
    ) {
      return;
    }

    const audioStream = await this.statusBoardRepository.getSpeechForReadyOrder(
      this.storeid,
      order.order_no
    );
    const buffer = Buffer.from(audioStream);
    const audioContext = new AudioContext();
    const audioBufferSource = audioContext.createBufferSource();
    audioBufferSource.connect(audioContext.destination);
    const audioBuffer = await audioContext.decodeAudioData(buffer.buffer);
    audioBufferSource.buffer = audioBuffer;
    audioBufferSource.start(0);
  }

  private async handleOrderChanged(data: any) {
    if (this.mainStationRepository === undefined) {
      return;
    }

    console.log(`Receive order from main station: ${data.content}`);

    const order = await this.mainStationRepository.loadOrderById(data.content);

    if (order !== null && order !== undefined) {
      const index = this.orders.findIndex(o => o.id === order.id);
      if (index < 0) {
        this.orders.push(order);
      } else {
        this.orders[index] = order;

        try {
          this.playSoundForOrder(order);
        } catch (error) {
          console.log(error);
        }
      }
      this.orders = [...this.orders];
    } else {
      const index = this.orders.findIndex(o => o.id === data.content);
      if (index > 0) {
        this.orders.splice(index, 1);
      }
      this.orders = [...this.orders];
    }
  }

  private async handleActiveShiftChanged() {
    await this.listOrders();
  }
}

decorate(ScreenStore, {
  viewState: observable,
  screen: observable,
  orders: observable
});

interface ScreenProviderProps {
  children: ReactElement;
  screenRepository: IScreenRepository;
  storeid: string;
  screenid: string;
  stationRepository?: IStationRepository;
  statusBoardRepository?: IStatusBoardRepository;
}

export const ScreenProvider: FC<ScreenProviderProps> = ({
  children,
  screenRepository,
  stationRepository,
  statusBoardRepository,
  storeid,
  screenid
}: ScreenProviderProps) => {
  const store = useLocalStore(
    () =>
      new ScreenStore(
        storeid,
        screenid,
        screenRepository,
        stationRepository,
        statusBoardRepository
      )
  );
  store.reload();
  return (
    <ScreenContext.Provider value={store}>{children}</ScreenContext.Provider>
  );
};

export const ScreenContext = createContext(new ScreenStore('', ''));
export const useScreen = () => useContext(ScreenContext);
